import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import ConsultationForm from "@/components/forms/ConsultationForm";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const services = [
    {
      title: "Formation",
      description: "UAE mainland and free zone company formation services",
      href: "/services/incorporation",
    },
    {
      title: "Bank Account Opening",
      description: "Corporate bank account setup with partner banks",
      href: "/services/banking",
    },
    {
      title: "VAT & Corporate Tax Registration",
      description: "Tax compliance and registration services",
      href: "/services/tax",
    },
    {
      title: "Golden Visa",
      description: "Long-term residence visa application and processing",
      href: "/services/golden-visa",
    },
    {
      title: "Visa Assistance",
      description: "Residence visas, employment visas, and family visas",
      href: "/services/visa",
    },
    {
      title: "Accounting",
      description: "Bookkeeping, financial reporting, and compliance",
      href: "/services/accounting",
    },
    {
      title: "IT Services",
      description: "Business technology solutions and support",
      href: "/services/it",
    },
  ];

  const [showConsultationForm, setShowConsultationForm] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-100 shadow-sm">
      <div className="container flex items-center justify-between h-16 px-4 mx-auto">
        {/* Logo (Left-aligned) */}
        <Link to="/" className="flex items-center">
          <span className="text-2xl font-bold text-primary">Cordi</span>
        </Link>

        {/* Navigation Menu (Desktop) */}
        <div className="hidden md:flex items-center space-x-6">
          <Link to="/" className="text-sm font-medium hover:text-primary">
            Home
          </Link>

          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-medium">
                  Services
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid gap-3 p-4 w-[400px] md:w-[500px] lg:w-[600px] lg:grid-cols-2">
                    {services.map((service) => (
                      <Link
                        key={service.href}
                        to={service.href}
                        className="block"
                      >
                        <div className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                          <div className="text-sm font-medium leading-none">
                            {service.title}
                          </div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            {service.description}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          <Link to="/faqs" className="text-sm font-medium hover:text-primary">
            FAQs
          </Link>

          <Button
            onClick={() => setShowConsultationForm(true)}
            className="bg-coral-600 hover:bg-coral-700"
            size="sm"
          >
            Talk to us
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px] p-0">
              <div className="flex flex-col h-full">
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-primary">
                      Cordi
                    </span>
                  </div>
                </div>

                <div className="flex-1 overflow-auto py-2">
                  <div className="px-4 py-2">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">
                      Quick Links
                    </h3>
                    <div className="space-y-1">
                      <Link
                        to="/"
                        className="block py-2 px-3 rounded-md text-sm hover:bg-accent"
                      >
                        Home
                      </Link>
                      <div className="py-2 px-3 text-sm font-medium">
                        Services
                      </div>
                      {services.map((service) => (
                        <Link
                          key={service.href}
                          to={service.href}
                          className="block py-2 px-6 rounded-md text-sm hover:bg-accent ml-3"
                        >
                          {service.title}
                        </Link>
                      ))}
                      <Link
                        to="/faqs"
                        className="block py-2 px-3 rounded-md text-sm hover:bg-accent"
                      >
                        FAQs
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="p-4 border-t">
                  <Button
                    onClick={() => setShowConsultationForm(true)}
                    className="flex items-center justify-center w-full py-2 text-sm font-medium bg-coral-600 hover:bg-coral-700 text-white rounded-md"
                  >
                    Talk to us
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Consultation Form */}
      <ConsultationForm
        open={showConsultationForm}
        onOpenChange={setShowConsultationForm}
      />
    </header>
  );
};

export default Header;
